import React, { useEffect, useRef } from "react";
import { LoadScript } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useGetMapDetailsQuery } from "../api/apiSlice";
import { Box, Typography, CircularProgress } from "@mui/material";
import config from "../../config";
const BG = "#0d1117";
const CARD_BG = "#161B22";
const BORDER = "rgba(255,255,255,0.07)";
const AMBER = "#FDB940";
const LABEL = "rgba(255,255,255,0.4)";

const DARK_MAP_STYLE = [
  { elementType: "geometry",                                       stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon",                                    stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.stroke",                             stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.text.fill",                               stylers: [{ color: "#424242" }] },
  { featureType: "administrative",          elementType: "geometry.stroke",      stylers: [{ color: "#bdbdbd" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill",     stylers: [{ color: "#212121" }] },
  { featureType: "administrative.country",  elementType: "labels.text.fill",     stylers: [{ color: "#616161" }] },
  { featureType: "poi",                     elementType: "geometry",              stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi",                     elementType: "labels.text.fill",     stylers: [{ color: "#757575" }] },
  { featureType: "poi.park",                elementType: "geometry",              stylers: [{ color: "#d8ead3" }] },
  { featureType: "road",                    elementType: "geometry",              stylers: [{ color: "#ffffff" }] },
  { featureType: "road",                    elementType: "geometry.stroke",       stylers: [{ color: "#e0e0e0" }] },
  { featureType: "road",                    elementType: "labels.text.fill",     stylers: [{ color: "#616161" }] },
  { featureType: "road.highway",            elementType: "geometry",              stylers: [{ color: "#e8d44d" }] },
  { featureType: "road.highway",            elementType: "geometry.stroke",       stylers: [{ color: "#c8b400" }] },
  { featureType: "road.highway",            elementType: "labels.text.fill",     stylers: [{ color: "#212121" }] },
  { featureType: "transit",                 elementType: "geometry",              stylers: [{ color: "#e5e5e5" }] },
  { featureType: "water",                   elementType: "geometry",              stylers: [{ color: "#b3d9ff" }] },
  { featureType: "water",                   elementType: "labels.text.fill",     stylers: [{ color: "#0277bd" }] },
];

function StatRow({ color, label, count }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: "7px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem" }}>{label}</Typography>
      </Box>
      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.82rem", fontVariantNumeric: "tabular-nums" }}>
        {count.toLocaleString()}
      </Typography>
    </Box>
  );
}

function Sidebar({ devices, noGpsCount, noJobCardCount, unavailable }) {
  const online = devices.filter((d) => d.connected).length;
  const offline = devices.length - online;
  // The per-cluster list is gone: it grouped by cluster_name, which no pin carries, so it
  // never showed (NEU-1131; the IMS map's went the same way in NEU-1095).

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        bgcolor: CARD_BG,
        borderRight: `1px solid ${BORDER}`,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box sx={{ p: "20px 16px 4px" }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>Field devices</Typography>
        <Typography sx={{ color: LABEL, fontSize: "0.75rem", mt: "2px" }}>
          {devices.length.toLocaleString()} devices on map
        </Typography>
      </Box>

      {/* Summary stats */}
      <Box sx={{ px: "16px", pb: "8px" }}>
        <StatRow color="#22C55E" label="Online Connected" count={online} />
        <StatRow color="#f87171" label="Offline Disconnected" count={offline} />
        {noGpsCount > 0 && (
          <StatRow color="#FDB940" label="No GPS (not on map)" count={noGpsCount} />
        )}
        {/* Installed devices with no matching job card were counted nowhere (NEU-1131). */}
        {noJobCardCount > 0 && (
          <StatRow color="#FDB940" label="No job card (not on map)" count={noJobCardCount} />
        )}
      </Box>

      {/* A backend that didn't answer, named, so short counts aren't read as complete. The
          map showed what it got and said nothing (NEU-1131). */}
      {unavailable.length > 0 && (
        <Box sx={{ mx: "16px", mb: "12px", p: "10px 12px", borderRadius: "8px", bgcolor: "rgba(253,185,64,0.08)", border: "1px solid rgba(253,185,64,0.35)" }}>
          <Typography sx={{ color: AMBER, fontSize: "0.78rem", fontWeight: 700 }}>Some devices may be missing</Typography>
          {unavailable.map((u, i) => (
            <Typography key={i} sx={{ color: LABEL, fontSize: "0.72rem", mt: "4px" }}>
              {(u.service || "").toUpperCase()}{u.backend ? ` · ${u.backend}` : ""} — {u.reason}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function OperatorMapOverview() {
  const { data: mapResponse, isSuccess, isLoading } = useGetMapDetailsQuery();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", bgcolor: BG }}>
        <CircularProgress sx={{ color: AMBER }} />
      </Box>
    );
  }

  if (!isSuccess || !mapResponse) return null;

  const address_data = mapResponse.devices ?? mapResponse;
  const no_gps_count = mapResponse.no_gps_count ?? 0;
  const no_job_card_count = mapResponse.no_job_card_count ?? 0;
  const unavailable = mapResponse.unavailable ?? [];

  return (
    <Box sx={{ height: "100vh", display: "flex", bgcolor: BG, overflow: "hidden" }}>
      <Sidebar devices={address_data} noGpsCount={no_gps_count} noJobCardCount={no_job_card_count} unavailable={unavailable} />
      <Box sx={{ flex: 1, position: "relative" }}>
        <LoadScript googleMapsApiKey={config.REACT_APP_GOOGLE_API_KEY}>
          <MapWithPins coordinates={address_data} />
        </LoadScript>

        {/* Cluster colour legend */}
        <Box
          sx={{
            position: "absolute",
            bottom: "24px",
            left: "16px",
            bgcolor: "rgba(22,27,34,0.92)",
            border: `1px solid ${BORDER}`,
            borderRadius: "8px",
            px: "12px",
            py: "10px",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ color: LABEL, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", mb: "6px" }}>
            Cluster colour · majority rule
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "4px" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22C55E" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem" }}>Mostly connected</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f87171" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem" }}>Mostly disconnected</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function MapWithPins({ coordinates }) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: -33.961, lng: 25.6007 },
      zoom: 13,
      styles: DARK_MAP_STYLE,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    const markers = coordinates.map((coord) => {
      const color = coord.connected ? "#22C55E" : "#f87171";
      const strokeColor = coord.connected ? "#16a34a" : "#dc2626";

      const marker = new window.google.maps.Marker({
        position: { lat: coord.latitude, lng: coord.longitude },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.9,
          scale: 7,
          strokeWeight: 1.5,
          strokeColor,
        },
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="background:#161B22;color:#fff;padding:8px 12px;border-radius:8px;font-size:13px;font-weight:500;border:1px solid rgba(255,255,255,0.1);">${coord.address || "No address"}</div>`,
      });

      marker.addListener("mouseover", () => infoWindow.open(map, marker));
      marker.addListener("mouseout", () => infoWindow.close());

      return marker;
    });

    const getClusterColor = (clusterMarkers) => {
      const connectedCount = clusterMarkers.filter(
        (m) => m.getIcon().fillColor === "#22C55E"
      ).length;
      return connectedCount >= clusterMarkers.length / 2 ? "#22C55E" : "#f87171";
    };

    const clusterRenderer = ({ count, position, markers: clusterMarkers, map: m }) => {
      const color = getClusterColor(clusterMarkers);
      const scale = Math.min(14 + count / 60, 40);
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.85,
          scale,
          strokeWeight: 2,
          strokeColor: color === "#22C55E" ? "#16a34a" : "#dc2626",
        },
        label: {
          text: String(count),
          color: "#fff",
          fontWeight: "700",
          fontSize: "11px",
        },
        map: m,
      });
    };

    new MarkerClusterer({
      map,
      markers,
      minClusterSize: 5,
      renderer: { render: clusterRenderer },
    });
  }, [coordinates]);

  return <div ref={mapRef} style={{ width: "100%", height: "100%" }} />;
}
